import React, { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { forgot } from "../../lib/authApi";
import { ApiError } from "../../lib/api";
import Wordmark from "../../components/Brand/Wordmark";

/*
 * Velaigal /forgot-password — requests a reset link.
 * The server always returns the same uniform message whether or not the
 * email exists (no enumeration), so the success panel below is shown for
 * every successful submit — only network/validation/rate-limit failures
 * toast instead of advancing past the form.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await forgot(email);
      setMessage(res.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not send reset link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-cream">
      {/* Left half — editorial framing, matches LoginPage's wrapper. */}
      <aside className="relative bg-cream px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between">
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-8 right-10 select-none pointer-events-none"
          style={{
            fontFamily: '"Noto Serif Tamil", Georgia, serif',
            fontSize: "72px",
            fontWeight: 500,
            color: "var(--color-ink)",
            opacity: 0.18,
            lineHeight: 1,
          }}
        >
          வே
        </span>

        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline self-start relative z-10"
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="lg:max-w-md mt-12 lg:mt-0 relative z-10">
          <h1
            className="font-display text-3xl lg:text-5xl font-medium text-ink leading-tight tracking-tight velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Forgot your <span className="text-saffron">password</span>?
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary leading-relaxed">
            Tell us the email on your account and we will send a link to
            reset it.
          </p>
        </div>

        <span className="hidden lg:block text-[11px] uppercase tracking-widest text-ink-tertiary mt-12">
          Made for hiring teams and jobseekers across the World.
        </span>
      </aside>

      {/* Right half — form / uniform-message panel. */}
      <section className="bg-paper flex items-center justify-center px-6 py-12 lg:px-16 lg:py-20 border-t lg:border-t-0 lg:border-l border-hairline">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary">
              Reset password
            </span>
          </div>

          {message ? (
            <div className="text-center space-y-6">
              <h2 className="font-display text-2xl font-medium text-ink tracking-tight">
                Check your email.
              </h2>
              <p className="text-sm text-ink-secondary leading-relaxed">{message}</p>
              <Link to="/login" className="btn-primary w-full">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-ink">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="input"
                />
              </div>

              <button type="submit" disabled={busy} className="btn-primary w-full">
                {busy ? "Sending…" : "Send reset link"}
              </button>

              <p className="text-center text-sm text-ink-tertiary">
                <Link to="/login" className="velaigal-link font-medium">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForgotPassword;
